/**
 * ServiceCheckoutModal — VARIANT 2 (service) of the 3-variant checkout.
 *
 * 3-step flow: reservation details (date picker + time range +
 * guestCount + deposit/balance breakdown) → summary → 2FA → create.
 *
 * SPEC-081 Fase 5 §6 Task C + §4.1 (service flow) + §0 Q4 (date picker
 * single day) + Q5 (free integer guestCount).
 *
 * The buyer reserves with a 50% deposit (debited to escrow on create).
 * The remaining 50% balance is charged at completion (off-platform —
 * handled by `completeServiceOrder` Task B1).
 *
 * SPEC-244 · partición VERBATIM de
 * frontend/src/components/market/checkout-modal.tsx (2.637 L).
 *
 * El monolito original mezclaba estado + handlers + render en un único
 * componente de ~596 L. Esta vista fina se compone de:
 *   - `useServiceCheckout` hook (toda la lógica de estado + handlers).
 *   - `ServiceDetailsStep` (Step 1 — date, time range, guests, deposit
 *     breakdown).
 *   - `ServiceSummaryStep` (Step 2 — reservation summary + payment
 *     breakdown + escrow note).
 *   - `ServiceVerifyStep` (Step 3 — 2FA-verified banner + create CTA).
 *   - `StepIndicator` + `TwoFaCheckoutDialog` (shared).
 *
 * Behavior preserved VERBATIM — only the wrapper extracts the 3 step
 * bodies into sub-components (T-PROPS-PASSING) + state into a hook
 * (T-HOOK-EXTRACTION).
 */

'use client';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Calendar } from 'lucide-react';
import type { CheckoutModalProps } from '../types';
import { useServiceCheckout } from '../hooks/use-service-checkout';
import { StepIndicator } from './step-indicator';
import { ServiceDetailsStep } from './service-details-step';
import { ServiceSummaryStep } from './service-summary-step';
import { ServiceVerifyStep } from './service-verify-step';

// ─── Component ───────────────────────────────────────────────────────────────

export function ServiceCheckoutModal({
  open,
  onClose,
  product,
}: CheckoutModalProps) {
  const checkout = useServiceCheckout({ open, onClose, product });
  const {
    step,
    setStep,
    reservationDate,
    setReservationDate,
    reservationTimeStart,
    setReservationTimeStart,
    reservationTimeEnd,
    setReservationTimeEnd,
    guestCount,
    setGuestCount,
    submitting,
    submitError,
    setSubmitError,
    depositPercent,
    grandTotal,
    depositAmount,
    balanceAmount,
    detailsValid,
    callCreate,
    handleOpenChange,
  } = checkout;

  return (
    <>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent
          className="sm:max-w-lg max-h-[90vh] overflow-y-auto"
          aria-describedby="checkout-service-modal-desc"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="size-5" aria-hidden="true" />
              {step === 'details' && 'Reservar servicio — Detalles'}
              {step === 'summary' && 'Reservar servicio — Resumen'}
              {step === 'verify' && 'Reservar servicio — Confirmar'}
            </DialogTitle>
            <DialogDescription id="checkout-service-modal-desc">
              {step === 'details' &&
                `Selecciona fecha, rango horario y número de invitados. Pagas un depósito del ${depositPercent}% ahora; el balance se carga al completar el servicio.`}
              {step === 'summary' &&
                'Revisa los detalles de tu reserva antes de continuar.'}
              {step === 'verify' &&
                'Confirma para crear la reserva y debitar el depósito al escrow.'}
            </DialogDescription>
          </DialogHeader>

          {/* Step indicator (1·2·3) */}
          <StepIndicator step={step} firstLabel="Reserva" />

          {/* ── Step 1: Reservation details ── */}
          {step === 'details' && (
            <ServiceDetailsStep
              product={product}
              reservationDate={reservationDate}
              setReservationDate={setReservationDate}
              reservationTimeStart={reservationTimeStart}
              setReservationTimeStart={setReservationTimeStart}
              reservationTimeEnd={reservationTimeEnd}
              setReservationTimeEnd={setReservationTimeEnd}
              guestCount={guestCount}
              setGuestCount={setGuestCount}
              depositPercent={depositPercent}
              grandTotal={grandTotal}
              depositAmount={depositAmount}
              balanceAmount={balanceAmount}
              detailsValid={detailsValid}
              submitError={submitError}
              onClose={onClose}
              onContinue={() => {
                setSubmitError(null);
                setStep('summary');
              }}
            />
          )}

          {/* ── Step 2: Summary ── */}
          {step === 'summary' && (
            <ServiceSummaryStep
              product={product}
              reservationDate={reservationDate}
              reservationTimeStart={reservationTimeStart}
              reservationTimeEnd={reservationTimeEnd}
              guestCount={guestCount}
              depositPercent={depositPercent}
              depositAmount={depositAmount}
              submitting={submitting}
              submitError={submitError}
              onBack={() => setStep('details')}
              onContinue={() => setStep('verify')}
              onClose={onClose}
            />
          )}

          {/* ── Step 3: Verify (after 2FA) ── */}
          {step === 'verify' && (
            <ServiceVerifyStep
              depositPercent={depositPercent}
              depositAmount={depositAmount}
              balanceAmount={balanceAmount}
              submitting={submitting}
              submitError={submitError}
              onBack={() => setStep('summary')}
              onCreate={() => void callCreate()}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
